import React, { useState } from "react";
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
} from "react-native";

import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { Ionicons } from "@expo/vector-icons";
import { useDevice, useScreenDimensions } from "../_layout";

import { useTranslation } from "react-i18next";
import GlobalStyles from "../../Resources/Styles";
import AthleteAppShowcaseBlockList from "../../assets/AthleteAppShowcaseBlockList.webp";
import AthleteAppShowcaseBlockView from "../../assets/AthleteAppShowcaseBlockView.webp";
import AthleteAppShowcaseChat from "../../assets/AthleteAppShowcaseChat.webp";
import AthleteAppShowcaseCompetition from "../../assets/AthleteAppShowcaseCompetition.webp";
import AthleteAppShowcaseExerciseHistory from "../../assets/AthleteAppShowcaseExerciseHistory.webp";
import AthleteAppShowcaseForm from "../../assets/AthleteAppShowcaseForm.webp";
import AthleteAppShowcaseHome from "../../assets/AthleteAppShowcaseHome.webp";
import AthleteAppShowcaseNutrition from "../../assets/AthleteAppShowcaseNutrition.webp";
import AthleteAppShowcaseWorkout from "../../assets/AthleteAppShowcaseWorkout.webp";
import LogoApp from "../../assets/LogoApp.jpg";

const AthleteAppOnboardingModal = ({
  isVisible,
  onCompleted,
  onClose,
  email,
  isPreviewMode = false,
  hideCloseButton = false,
  isAlreadyCompleted = false,
}) => {
  const screenSize = useScreenDimensions();
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hoveredDot, setHoveredDot] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);

  const scrollViewRef = React.useRef(null);
  const isDragging = React.useRef(false);
  const startX = React.useRef(0);
  const startScrollX = React.useRef(0);
  const { isMobile } = useDevice();
  // Images for onboarding modal
  const images = [
    {
      id: 1,
      source: AthleteAppShowcaseHome,
      title: t("athleteAppOnboardingHome-string"),
    },
    {
      id: 2,
      source: AthleteAppShowcaseBlockList,
      title: t("athleteAppOnboardingBlocks-string"),
    },
    {
      id: 3,
      source: AthleteAppShowcaseWorkout,
      title: t("athleteAppOnboardingWorkout-string"),
    },
    {
      id: 4,
      source: AthleteAppShowcaseChat,
      title: t("athleteAppOnboardingChat-string"),
    },
    {
      id: 5,
      source: AthleteAppShowcaseBlockView,
      title: t("athleteAppOnboardingBlockView-string"),
    },
    {
      id: 6,
      source: AthleteAppShowcaseExerciseHistory,
      title: t("athleteAppOnboardingExerciseHistory-string"),
    },
    {
      id: 7,
      source: AthleteAppShowcaseForm,
      title: t("athleteAppOnboardingForms-string"),
    },
    {
      id: 8,
      source: AthleteAppShowcaseNutrition,
      title: t("athleteAppOnboardingNutrition-string"),
    },
    {
      id: 9,
      source: AthleteAppShowcaseCompetition,
      title: t("athleteAppOnboardingCompetitions-string"),
    },
  ];

  const IMAGE_WIDTH = 185; // Default width for each image
  const IMAGE_SPACING = 12; // Spacing between images

  const handleMarkAsCompleted = () => {
    if (!isAlreadyCompleted) {
      onCompleted?.();
      onClose?.();
    }
  };

  const [canScrollRight, setCanScrollRight] = useState(true);

  const handleScroll = (event) => {
    const scrollPosition = event.nativeEvent.contentOffset.x;
    const contentWidth = event.nativeEvent.contentSize.width;
    const containerWidth = event.nativeEvent.layoutMeasurement.width;

    // Store current scroll position for dragging
    startScrollX.current = scrollPosition;

    // Check if we can scroll more to the right
    const maxScroll = contentWidth - containerWidth;
    setCanScrollRight(scrollPosition < maxScroll - 5); // 5px threshold

    // Calculate which image is most visible on the left side of the screen
    const index = Math.round(scrollPosition / (IMAGE_WIDTH + IMAGE_SPACING));
    const clampedIndex = Math.max(0, Math.min(index, images.length - 1));

    setCurrentImageIndex(clampedIndex);

    // Calculate scroll progress (0 to 1)
    // (scroll progress not currently displayed)
  };

  const handleMouseDown = (event) => {
    isDragging.current = true;
    startX.current = event.nativeEvent.pageX;
    // startScrollX is already set by handleScroll
  };

  const handleMouseMove = (event) => {
    if (!isDragging.current) return;
    event.preventDefault();
    const x = event.nativeEvent.pageX;
    const walk = (startX.current - x) * 0.1; // Reduced sensitivity (0.5x speed)
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({
        x: startScrollX.current + walk,
        animated: false,
      });
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleNextImage = () => {
    const nextIndex = Math.min(currentImageIndex + 1, images.length - 1);
    if (scrollViewRef.current && nextIndex !== currentImageIndex) {
      scrollViewRef.current.scrollTo({
        x: nextIndex * (IMAGE_WIDTH + IMAGE_SPACING),
        animated: true,
      });
    }
  };

  const handlePreviousImage = () => {
    const prevIndex = Math.max(currentImageIndex - 1, 0);
    if (scrollViewRef.current && prevIndex !== currentImageIndex) {
      scrollViewRef.current.scrollTo({
        x: prevIndex * (IMAGE_WIDTH + IMAGE_SPACING),
        animated: true,
      });
    }
  };

  const handleDotPress = (index) => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({
        x: index * (IMAGE_WIDTH + IMAGE_SPACING),
        animated: true,
      });
    }
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={onClose}
    >
      <View style={GlobalStyles.centeredView}>
        <TouchableOpacity
          onPress={onClose}
          style={GlobalStyles.touchableBackground}
        >
          <View />
        </TouchableOpacity>
        <View
          style={{
            position: "absolute",
            maxWidth: "90%",
            maxHeight: "95%",
            // width: "90%",
            // height: "95%",
            justifyContent: "center",
          }}
          onStartShouldSetResponder={() => false}
        >
          <View
            style={[
              styles.modalView,
              {
                padding: 24,
                alignSelf: "center",
                width: "100%",
                height: "100%",
                maxHeight: 700,
                maxWidth: 900,
                justifyContent: "space-between",
                flexShrink: 1,
              },
            ]}
          >
            {/* Close button */}
            {!hideCloseButton && (
              <TouchableOpacity
                style={styles.closeButton}
                onPress={onClose}
                onMouseEnter={() => !isMobile && setHoveredButton("close")}
                onMouseLeave={() => setHoveredButton(null)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name="close"
                  size={24}
                  color={
                    hoveredButton === "close"
                      ? Colors.blackColor
                      : Colors.grayColor
                  }
                />
              </TouchableOpacity>
            )}

            {/* Title */}
            <Text
              style={[
                screenSize === "small" ? Fonts.medium18 : Fonts.medium20,
                {
                  textAlign: "center",
                  color: Colors.blackColor,
                  marginBottom: 24,
                  flexShrink: 0,
                  marginHorizontal: 32,
                },
              ]}
            >
              {isPreviewMode
                ? t("athleteAppPreviewTitle-string")
                : t("athleteAppOnboardingTitle-string")}
            </Text>

            {/* Scrollable content area */}
            <ScrollView
              style={styles.contentScrollView}
              contentContainerStyle={styles.contentScrollViewContent}
              showsVerticalScrollIndicator={false}
            >
              {/* Image Carousel */}
              <View style={styles.carouselWrapper}>
                <View
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <ScrollView
                    ref={scrollViewRef}
                    horizontal
                    pagingEnabled={false}
                    showsHorizontalScrollIndicator={false}
                    onScroll={handleScroll}
                    scrollEventThrottle={16}
                    style={[
                      styles.carouselContainer,
                      { width: "100%", paddingBottom: 16 },
                    ]}
                    contentContainerStyle={[
                      styles.carouselContent,
                      { gap: IMAGE_SPACING },
                    ]}
                    snapToInterval={IMAGE_WIDTH + IMAGE_SPACING}
                    decelerationRate="fast"
                  >
                    {images.map((image) => (
                      <View
                        key={image.id}
                        style={[styles.imageContainer, { width: IMAGE_WIDTH }]}
                      >
                        <View style={styles.imageClipper}>
                          <Image
                            source={image.source}
                            style={styles.image}
                            resizeMode="cover"
                          />
                        </View>
                        {/* Overlay with title */}
                        <View style={styles.imageOverlay}>
                          <View style={styles.overlayTitleContainer}>
                            <Text style={styles.overlayTitle}>
                              {image.title}
                            </Text>
                          </View>
                        </View>
                      </View>
                    ))}
                    {/* Add spacing at the end so last image can be centered */}
                  </ScrollView>
                </View>

                {/* Previous button - only show if not at first image */}
                {currentImageIndex > 0 && (
                  <TouchableOpacity
                    style={[
                      styles.previousButton,
                      hoveredButton === "prev" && styles.arrowButtonHovered,
                    ]}
                    onPress={handlePreviousImage}
                    onMouseEnter={() => !isMobile && setHoveredButton("prev")}
                    onMouseLeave={() => setHoveredButton(null)}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons
                      name="chevron-back"
                      size={24}
                      color={Colors.whiteColor}
                    />
                  </TouchableOpacity>
                )}

                {/* Next button - only show if not at last image */}
                {canScrollRight && (
                  <TouchableOpacity
                    style={[
                      styles.nextButton,
                      hoveredButton === "next" && styles.arrowButtonHovered,
                    ]}
                    onPress={handleNextImage}
                    onMouseEnter={() => !isMobile && setHoveredButton("next")}
                    onMouseLeave={() => setHoveredButton(null)}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons
                      name="chevron-forward"
                      size={24}
                      color={Colors.whiteColor}
                    />
                  </TouchableOpacity>
                )}
              </View>

              {/* Progress bar */}
              <View style={[styles.progressBarContainer]}>
                <View style={styles.paginationContainer}>
                  {images.map((_, index) => {
                    const isHovered = hoveredDot === index;
                    const isActive = currentImageIndex === index;
                    return (
                      <TouchableOpacity
                        key={index}
                        onPress={() => handleDotPress(index)}
                        onMouseEnter={() => !isMobile && setHoveredDot(index)}
                        onMouseLeave={() => setHoveredDot(null)}
                        style={[
                          styles.dot,
                          {
                            backgroundColor: isActive
                              ? Colors.bubbleColor
                              : Colors.grayColor,
                            opacity: isActive ? 1 : 0.3,
                            transform: [{ scale: isHovered ? 1.5 : 1 }],
                            cursor: "pointer",
                          },
                        ]}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      />
                    );
                  })}
                </View>
              </View>
            </ScrollView>

            {/* Instructions text */}
            {!isPreviewMode && (
              <View style={styles.instructionsContainer}>
                <Text
                  style={[
                    screenSize === "small" ? Fonts.regular14 : Fonts.regular15,
                    {
                      textAlign: "center",
                      color: Colors.grayColor,
                      lineHeight: 20,
                    },
                  ]}
                >
                  {t("athleteAppOnboarding1-string")}
                  <Image
                    source={LogoApp}
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      marginLeft: 1,
                      marginRight: 4,
                      marginBottom: -2,
                    }}
                    resizeMode="cover"
                  />
                  {t("athleteAppOnboarding2-string")}
                </Text>
                <Text
                  style={[
                    screenSize === "small" ? Fonts.medium14 : Fonts.medium15,
                    {
                      textAlign: "center",
                      color: Colors.bubbleColor,
                      marginTop: 8,
                    },
                  ]}
                >
                  {email}
                </Text>
              </View>
            )}

            {/* Mark as completed button */}
            {!isPreviewMode && (
              <TouchableOpacity
                style={[
                  styles.completedButton,
                  isAlreadyCompleted && styles.completedButtonDisabled,
                  hoveredButton === "completed" &&
                    !isAlreadyCompleted &&
                    styles.completedButtonHovered,
                ]}
                onPress={handleMarkAsCompleted}
                onMouseEnter={() => !isMobile && setHoveredButton("completed")}
                onMouseLeave={() => setHoveredButton(null)}
                disabled={isAlreadyCompleted}
              >
                <Text
                  style={[
                    {
                      color: isAlreadyCompleted
                        ? Colors.grayColor
                        : Colors.whiteColor,
                      fontSize: screenSize === "small" ? 14 : 15,
                      fontWeight: "500",
                    },
                  ]}
                >
                  {isAlreadyCompleted
                    ? t("completed-string")
                    : t("athleteAppOnboarding-markCompleted")}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalView: {
    alignSelf: "center",
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },

  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: 10,
    padding: 8,
  },

  carouselWrapper: {
    position: "relative",
    // marginBottom: 16,
    flexShrink: 1,
    minHeight: 200,
  },

  contentScrollView: {
    flex: 1,
    minHeight: 0,
  },

  contentScrollViewContent: {
    flexGrow: 1,
    justifyContent: "center",
  },

  carouselContainer: {
    flexGrow: 0,
    flexShrink: 1,
  },

  carouselContent: {
    alignItems: "center",
  },

  imageContainer: {
    aspectRatio: 585 / 1266,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.whiteColor,
    borderRadius: 22,
    borderWidth: 2,
    borderColor: "#C8CED3",
    position: "relative",
  },

  imageClipper: {
    position: "absolute",
    top: 2,
    right: 2,
    bottom: 2,
    left: 2,
    borderRadius: 20,
    overflow: "hidden",
  },

  image: {
    width: "100%",
    height: "100%",
    borderRadius: 20,
  },

  imageOverlay: {
    position: "absolute",
    // top: 0,
    left: 0,
    right: 0,
    bottom: -10,
    borderRadius: 12,
    justifyContent: "flex-start",
    alignItems: "center",
    // paddingTop: 16,
  },

  overlayTitleContainer: {
    backgroundColor: "rgba(255, 255, 255, 0.90)",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },

  overlayTitle: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },

  nextButton: {
    position: "absolute",
    right: 8,
    top: "50%",
    marginTop: -24,
    width: 40,
    height: 40,
    borderRadius: 24,
    backgroundColor: Colors.bubbleColor,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },

  previousButton: {
    position: "absolute",
    left: 8,
    top: "50%",
    marginTop: -24,
    width: 40,
    height: 40,
    borderRadius: 24,
    backgroundColor: Colors.bubbleColor,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },

  arrowButtonHovered: {
    opacity: 0.85,
    transform: [{ scale: 1.05 }],
  },

  progressBarContainer: {
    paddingHorizontal: 16,
    // paddingVertical: 16,
  },

  paginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },

  dot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    marginHorizontal: 3,
  },

  instructionsContainer: {
    marginVertical: 16,
    paddingHorizontal: 16,
    flexShrink: 0,
  },

  completedButton: {
    backgroundColor: Colors.bubbleColor,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-end",
    flexShrink: 0,
  },

  completedButtonDisabled: {
    backgroundColor: Colors.lightGrayColor,
  },

  completedButtonHovered: {
    opacity: 0.85,
    transform: [{ scale: 1.02 }],
  },
});

export default AthleteAppOnboardingModal;
